// @ts-nocheck baseline 2026-02-16
/**
 * Legacy Storage Migration
 *
 * Migrates auth data (token, userId, PIN) from native iOS/Android storage
 * to React Native SecureStore. Supports multiple migration triggers:
 *
 * - Initial migration on app startup
 * - Force migration on legacy → RN mode transition
 */

import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";
import { genericStorage } from "@src/services/app-storage";
import LegacyKit from "modules/legacy-kit";

const PREVIOUS_APP_MODE_KEY = "previous_app_mode" as const;
const LEGACY_MIGRATION_COMPLETED_KEY = "legacy_migration_completed" as const;

function hasCompletedLegacyMigration(): boolean {
  return genericStorage.get<boolean>(LEGACY_MIGRATION_COMPLETED_KEY) ?? false;
}

function markLegacyMigrationCompleted(): void {
  genericStorage.set(LEGACY_MIGRATION_COMPLETED_KEY, true);
}

function resetLegacyMigrationCompleted(): void {
  genericStorage.remove(LEGACY_MIGRATION_COMPLETED_KEY);
}

export interface LegacyAuthData {
  userId: string | null;
  email: string | null;
  token: string | null;
  pin: string | null;
}

interface NativeLegacyData {
  userId?: string;
  token?: string;
  pin?: string;
  email?: string;
}

/**
 * Reads all legacy auth data from SecureStore (for debugging/verification)
 */
export async function checkLegacyMigration(): Promise<LegacyAuthData> {
  const [userId, email, token, pin] = await Promise.all([
    secureStorage.get(SecureStorageKeys.legacyUserId),
    secureStorage.get(SecureStorageKeys.legacyUserEmail),
    secureStorage.get(SecureStorageKeys.legacyAuthToken),
    secureStorage.get(SecureStorageKeys.legacyUserPin),
  ]);
  return { userId, email, token, pin };
}

/**
 * Clears all legacy auth data from SecureStore
 */
export async function clearLegacyMigrationData(): Promise<void> {
  await Promise.all([
    secureStorage.remove(SecureStorageKeys.legacyUserId),
    secureStorage.remove(SecureStorageKeys.legacyUserEmail),
    secureStorage.remove(SecureStorageKeys.legacyAuthToken),
    secureStorage.remove(SecureStorageKeys.legacyUserPin),
  ]);
  resetLegacyMigrationCompleted();
}

/**
 * Core migration logic - fetches from native and stores in RN SecureStore
 */
async function performMigration(): Promise<boolean> {
  const legacyData = LegacyKit.getLegacyAuthData() as NativeLegacyData | null;

  if (!legacyData?.token || !legacyData?.userId) {
    return false;
  }

  await Promise.all([
    secureStorage.set(SecureStorageKeys.legacyUserId, legacyData.userId),
    secureStorage.set(SecureStorageKeys.legacyAuthToken, legacyData.token),
    legacyData.pin ? secureStorage.set(SecureStorageKeys.legacyUserPin, legacyData.pin) : Promise.resolve(),
    legacyData.email ? secureStorage.set(SecureStorageKeys.legacyUserEmail, legacyData.email) : Promise.resolve(),
  ]);

  markLegacyMigrationCompleted();

  return true;
}

/**
 * Migrates legacy auth data from native keychain to RN SecureStore.
 * Called on every app launch to ensure credentials are up-to-date.
 * Always attempts migration to catch tokens created/updated in legacy app.
 */
export async function migrateLegacyAuthData(): Promise<boolean> {
  try {
    if (hasCompletedLegacyMigration()) {
      return false;
    }
    return await performMigration();
  } catch (error) {
    console.error("[LegacyMigration] Migration failed:", error);
    return false;
  }
}

/**
 * Force migration - overwrites existing RN data with native data.
 * Used when transitioning from legacy to RN mode.
 */
export async function forceMigrateLegacyAuthData(): Promise<boolean> {
  try {
    resetLegacyMigrationCompleted();
    return await performMigration();
  } catch (error) {
    console.error("[LegacyMigration] Force migration failed:", error);
    return false;
  }
}

/**
 * Tracks app mode transitions and triggers migration when switching legacy → RN.
 * @param showingLegacy - true if showing legacy app, false if showing RN
 * @returns true if migration was performed
 */
export async function handleAppModeTransition(showingLegacy: boolean): Promise<boolean> {
  try {
    const currentMode = showingLegacy ? "legacy" : "rn";
    const previousMode = genericStorage.get<string>(PREVIOUS_APP_MODE_KEY);

    genericStorage.set(PREVIOUS_APP_MODE_KEY, currentMode);

    // Legacy → RN transition: force migrate to get latest native data
    if (previousMode === "legacy" && currentMode === "rn") {
      return await forceMigrateLegacyAuthData();
    }

    // First time showing RN: regular migration
    if (previousMode === undefined && currentMode === "rn") {
      return await migrateLegacyAuthData();
    }

    return false;
  } catch (error) {
    console.error("[LegacyMigration] Mode transition failed:", error);
    return false;
  }
}
